import { Component, ErrorInfo, ReactNode } from 'react';
import { ErrorCard } from './ErrorCard';
import { useTranslation } from 'react-i18next';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

// Class component for basic error boundary
export class BasicErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex min-h-screen items-center justify-center p-4">
          <ErrorCard
            error={this.state.error}
            onRetry={() => this.setState({ hasError: false, error: null })}
          />
        </div>
      );
    }

    return this.props.children;
  }
}


// Main error boundary component
export function AppErrorBoundary({ children, onError }: { children: ReactNode; onError?: (error: Error) => void }) {
  return (
    <BasicErrorBoundary
      onError={onError}
      fallback={
        <div className="flex min-h-[400px] items-center justify-center p-4">
          <ErrorCard
            error={new Error('Something went wrong')}
            onRetry={() => window.location.reload()}
          />
        </div>
      }
    >
      {children}
    </BasicErrorBoundary>
  );
}

// Route-level error boundary with more context
export function RouteErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <BasicErrorBoundary
      fallback={<RouteErrorFallback />}
    >
      {children}
    </BasicErrorBoundary>
  );
}

// Route error fallback component
function RouteErrorFallback() {
  const { t } = useTranslation();
  
  return (
    <div className="container mx-auto p-6">
      <div className="mx-auto max-w-2xl">
        <ErrorCard
          error={new Error(t('errors.boundary.message'))}
          onRetry={() => window.location.reload()}
          showHomeButton
        />
        <div className="mt-4 rounded-lg border bg-gray-50 p-4">
          <h3 className="mb-2 font-medium text-gray-900">
            {t('errors.boundary.helpTitle')}
          </h3>
          <ul className="list-inside list-disc space-y-1 text-sm text-gray-600">
            <li>{t('errors.boundary.help1')}</li>
            <li>{t('errors.boundary.help2')}</li>
            <li>{t('errors.boundary.help3')}</li>
          </ul>
        </div>
      </div>
    </div>
  );
}